import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * PartnerAd — displays a single eligible partner ad (or nothing).
 * Always shows the "Sponsored" label. Clearly separated from the KT Milano routine.
 * Never renders above main content — always placed BELOW the result/plan/routine.
 */
export default function PartnerAd({
  placement,       // "free_result" | "paid_plan" | "salon_result" | "pdf_footer"
  language = "en",
  audience = "individual",  // "female" | "male" | "salon" | "individual"
  profile = "",
  country = "",
  salonSlug = "",
  title = "",      // optional override; defaults to i18n "Selected Partner"
}) {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/ads/serve`, {
        params: { placement, language, audience, profile, country, salon_slug: salonSlug },
      })
      .then((r) => setAd(r.data?.ad || null))
      .catch(() => setAd(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, language, audience, profile, country, salonSlug]);

  if (loading || !ad) return null;

  const heading = title || (language === "it" ? "Partner selezionato" : "Selected Partner");
  const clickUrl = `${API}${ad.destination_url}?placement=${encodeURIComponent(placement)}&language=${language}${salonSlug ? `&salon_slug=${salonSlug}` : ""}`;

  return (
    <div
      className="mt-12 rounded-3xl border kt-line bg-white kt-shadow overflow-hidden"
      data-testid={`partner-ad-${placement}`}
      data-campaign-id={ad.campaign_id}
    >
      <div className="flex items-center justify-between px-5 py-2 border-b kt-line" style={{ backgroundColor: "#F8F6F2" }}>
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{heading}</div>
        <div
          className="text-[9px] kt-tracking uppercase rounded-full px-2 py-0.5 border"
          style={{ borderColor: "#D8C4A5", color: "#6F6B66" }}
          data-testid="sponsored-label"
        >
          {ad.sponsored_label}
        </div>
      </div>

      <a
        href={clickUrl}
        target="_blank"
        rel="noopener sponsored"
        data-testid={`partner-ad-click-${placement}`}
        className="block hover:opacity-95 transition"
      >
        <div className="hidden sm:block">
          {ad.desktop_banner_url ? (
            <img
              src={ad.desktop_banner_url}
              alt={ad.brand_name}
              loading="lazy"
              decoding="async"
              className="w-full h-auto"
              data-testid="partner-ad-desktop-img"
            />
          ) : (
            <div className="p-6 flex items-center gap-4">
              {ad.logo_url && <img src={ad.logo_url} alt={ad.brand_name} loading="lazy" decoding="async" className="h-8 w-auto" />}
              <div className="flex-1">
                <div className="font-serif text-lg text-[#383633]">{ad.brand_name}</div>
                <div className="text-sm text-[#6F6B66]">{ad.copy}</div>
              </div>
            </div>
          )}
        </div>
        <div className="sm:hidden">
          {ad.mobile_banner_url ? (
            <img
              src={ad.mobile_banner_url}
              alt={ad.brand_name}
              loading="lazy"
              decoding="async"
              className="w-full h-auto"
              data-testid="partner-ad-mobile-img"
            />
          ) : (
            <div className="p-4">
              <div className="font-serif text-base text-[#383633]">{ad.brand_name}</div>
              <div className="text-xs text-[#6F6B66]">{ad.copy}</div>
            </div>
          )}
        </div>
      </a>
    </div>
  );
}
